import api from './api';
import {
  SkillMeta, SkillDetail, SkillListResponse, SkillSection,
  SourceConfig, SyncReport,
  SkillSearchParams, ExportPackRequest, ExportScaffoldRequest,
  SkillAttachmentInfo, SkillCreateRequest, SkillUpdateRequest,
} from '../types/skill';

async function withRetry<T>(fn: () => Promise<T>, maxAttempts = 3): Promise<T> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === maxAttempts) throw err;
    }
  }
  throw new Error('unreachable');
}

// ── Skills CRUD ───────────────────────────────────────────────────────────────

export async function listSkills(params?: Partial<SkillSearchParams>): Promise<SkillListResponse> {
  const response = await api.get('/skills/', { params });
  return response.data;
}

export async function getSkill(name: string): Promise<SkillDetail> {
  const response = await api.get(`/skills/${name}`);
  return response.data;
}

export async function getSkillRaw(name: string): Promise<string> {
  const response = await api.get(`/skills/${name}/raw`);
  return response.data;
}

export async function getSkillSections(name: string): Promise<SkillSection[]> {
  const response = await api.get(`/skills/${name}/sections`);
  return response.data;
}

export async function createSkill(req: SkillCreateRequest): Promise<SkillMeta> {
  const response = await api.post('/skills/', req);
  return response.data;
}

export async function updateSkill(name: string, req: SkillUpdateRequest): Promise<SkillMeta> {
  const response = await api.put(`/skills/${name}`, req);
  return response.data;
}

export async function deleteSkill(name: string): Promise<void> {
  await api.delete(`/skills/${name}`);
}

// ── Sources ───────────────────────────────────────────────────────────────────

export async function listSources(): Promise<SourceConfig[]> {
  const response = await api.get('/skills/sources/');
  return response.data;
}

export async function addSource(req: Partial<SourceConfig> & { auth_token?: string }): Promise<SourceConfig> {
  const response = await api.post('/skills/sources/', req);
  return response.data;
}

export async function syncSource(sourceId: string): Promise<any> {
  return withRetry(async () => {
    const response = await api.post(`/skills/sources/${sourceId}/sync`);
    return response.data;
  });
}

export async function syncAll(): Promise<any[]> {
  return withRetry(async () => {
    const response = await api.post('/skills/sources/sync-all');
    return response.data;
  });
}

export async function removeSource(sourceId: string): Promise<void> {
  await api.delete(`/skills/sources/${sourceId}`);
}

// ── Curation ──────────────────────────────────────────────────────────────────

export async function listPending(): Promise<SkillMeta[]> {
  const response = await api.get('/skills/pending');
  return response.data;
}

export async function approveSkill(name: string): Promise<SkillMeta> {
  const response = await api.post(`/skills/pending/${name}/approve`);
  return response.data;
}

export async function rejectSkill(name: string): Promise<void> {
  await api.post(`/skills/pending/${name}/reject`);
}

// ── Attachments ───────────────────────────────────────────────────────────────

export async function attachToPersona(personaId: string, skillNames: string[]): Promise<{ attached: string[] }> {
  const response = await api.post(`/skills/attach/persona/${personaId}`, { skill_names: skillNames });
  return response.data;
}

export async function detachFromPersona(personaId: string, skillName: string): Promise<void> {
  await api.delete(`/skills/detach/persona/${personaId}/${skillName}`);
}

export async function getSkillsForPersona(personaId: string): Promise<SkillAttachmentInfo[]> {
  const response = await api.get(`/skills/by-persona/${personaId}`);
  return response.data;
}

export async function attachToSession(sessionId: string, skillNames: string[]): Promise<{ attached: string[] }> {
  const response = await api.post(`/skills/attach/session/${sessionId}`, { skill_names: skillNames });
  return response.data;
}

export async function detachFromSession(sessionId: string, skillName: string): Promise<void> {
  await api.delete(`/skills/detach/session/${sessionId}/${skillName}`);
}

export async function getSkillsForSession(sessionId: string): Promise<SkillAttachmentInfo[]> {
  const response = await api.get(`/skills/by-session/${sessionId}`);
  return response.data;
}

// ── Export ────────────────────────────────────────────────────────────────────

export async function exportPack(request: ExportPackRequest): Promise<void> {
  const response = await api.post('/skills/export/pack', request, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${request.pack_name}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportScaffold(request: ExportScaffoldRequest): Promise<Record<string, string>> {
  const response = await api.post('/skills/export/scaffold', request);
  return response.data;
}

export async function updateSource(sourceId: string, data: Record<string, any>): Promise<any> {
  const response = await api.put(`/skills/sources/${sourceId}`, data);
  return response.data;
}

// ── Registry ──────────────────────────────────────────────────────────────────

export async function bulkDeleteSkills(skillNames: string[]): Promise<{ deleted: string[]; not_found: string[] }> {
  const response = await api.post('/skills/bulk-delete', { skill_names: skillNames });
  return response.data;
}

export async function rebuildRegistry(): Promise<{ skills_count: number; duration_ms: number }> {
  const response = await api.post('/skills/registry/rebuild');
  return response.data;
}

export async function getRegistryStats(): Promise<any> {
  const response = await api.get('/skills/registry/stats');
  return response.data;
}
